/**
 * Units: creation, lookup and the handful of derived values everything else asks for.
 *
 * Every list returned here is sorted by id. Object key order is *nearly* always
 * ascending for integer keys and relying on "nearly" is how a turn engine ends up
 * producing a different result on the second run of the same seed.
 */

import { traitSum, unitFor } from '../civs/traits.js';
import type { Content, UnitDef } from '../../../content/schema/index.js';
import { tileIndex } from '../state/grid.js';
import { NONE, type GameState, type Id, type TileGrid, type Unit } from '../state/types.js';

export function unitDef(content: Content, unit: Unit): UnitDef {
  return content.units.get(unit.type);
}

/** The rank a unit holds, clamped to the ranks content declares. */
export function rankOf(content: Content, unit: Unit): { index: number; name: string; hpBonus: number } {
  const ranks = content.rules.combat.ranks;
  const index = Math.max(0, Math.min(ranks.length - 1, unit.experience));
  return { index, ...ranks[index]! };
}

/** Full health for this unit: its type's hit points plus what its rank adds. */
export function maxHp(content: Content, unit: Unit): number {
  return unitDef(content, unit).hp + rankOf(content, unit).hpBonus;
}

/** Units aboard a ship, ascending by id. */
export function cargoOf(state: GameState, ship: Unit): Unit[] {
  return allUnits(state).filter((u) => u.aboard === ship.id);
}

/** Room left on a ship for soldiers. Zero for anything that carries nothing. Aircraft aboard do not count. */
export function spareCapacity(state: GameState, content: Content, ship: Unit): number {
  const soldiers = cargoOf(state, ship).filter((u) => unitDef(content, u).domain !== 'air').length;
  return Math.max(0, unitDef(content, ship).capacity - soldiers);
}

/** Room left on a carrier's deck (ADR-0066). */
export function spareAirCapacity(state: GameState, content: Content, ship: Unit): number {
  const planes = cargoOf(state, ship).filter((u) => unitDef(content, u).domain === 'air').length;
  return Math.max(0, unitDef(content, ship).airCapacity - planes);
}

/** A unit type's full movement allowance, in move points. */
export function maxMoves(content: Content, def: UnitDef): number {
  return def.movement * content.rules.movement.scale;
}

/** A unit's full movement, with its civilisation's traits: a seafaring people's ships go further (ADR-0062). */
export function maxMovesOf(state: GameState, content: Content, unit: Unit): number {
  const def = content.units.get(unit.type);
  const extra = def.domain === 'sea' ? traitSum(content, state.civs[unit.owner], 'seaMovement') : 0;
  return (def.movement + extra) * content.rules.movement.scale;
}

/** Plane index of the tile a unit is standing on. */
export function unitIndex(map: TileGrid, unit: Unit): number {
  return tileIndex(map, unit.col, unit.row);
}

function byId(a: Unit, b: Unit): number {
  return a.id - b.id;
}

export function allUnits(state: GameState): Unit[] {
  return Object.values(state.units).sort(byId);
}

// Filter first, then sort the few that match: the same list as sorting everything and
// filtering, for a fraction of the work — these two run thousands of times a turn (Phase 15).
export function unitsOf(state: GameState, civId: Id): Unit[] {
  return Object.values(state.units)
    .filter((u) => u.owner === civId)
    .sort(byId);
}

export function unitsAtIndex(state: GameState, index: number): Unit[] {
  const width = state.map.width;
  const col = index % width;
  const row = (index - col) / width;
  return Object.values(state.units)
    .filter((u) => u.col === col && u.row === row)
    .sort(byId);
}

/**
 * Units that can still act and have no standing order — the ones the "next unit"
 * cycle offers, and the ones end-turn warns about.
 *
 * Fortified and sleeping units are excluded: they have been given an instruction,
 * which is the whole point of those orders.
 */
export function unitsAwaitingOrders(state: GameState, civId: Id): Unit[] {
  return unitsOf(state, civId).filter((u) => u.movesLeft > 0 && u.orders === null && !u.automated);
}

/** Create a unit and add it to the state. Returns it. */
export function spawnUnit(
  state: GameState,
  content: Content,
  owner: Id,
  typeKey: string,
  col: number,
  row: number,
  rank = 0,
): Unit {
  // A civilisation with a unique unit gets that in place of the standard one (ADR-0062).
  const def = unitFor(content, state.civs[owner], content.units.get(content.units.id(typeKey)));
  const type = def.id;
  const unit: Unit = {
    id: state.nextId++,
    owner,
    type,
    col,
    row,
    hp: def.hp,
    movesLeft: (def.movement + (def.domain === 'sea' ? traitSum(content, state.civs[owner], 'seaMovement') : 0)) * content.rules.movement.scale,
    experience: rank,
    orders: null,
    aboard: NONE,
    automated: false,
  };
  unit.hp = maxHp(content, unit);
  state.units[unit.id] = unit;
  return unit;
}
